import React from "react";
import { Link } from "react-router-dom";
import { ContactEmail, PolicyPage, PolicySection } from "./PolicyPage";

function Privacy() {
  return (
    <PolicyPage
      title="Privacy Policy"
      intro="This policy explains what personal information Sacuddin collects through sacuddin.shop, why we use it, when we share it, and the choices available to you."
    >
      <PolicySection title="1. Scope and responsibility">
        <p>
          This Privacy Policy applies when you browse our website, place or attempt
          to place an order, contact us, subscribe to communications, or otherwise
          interact with Sacuddin online. Sacuddin is responsible for the personal
          information described here unless another service identifies itself as
          independently responsible for its processing.
        </p>
        <p>
          This policy does not cover third-party websites or services reached through
          our site. Their own privacy notices apply to their activities.
        </p>
      </PolicySection>

      <PolicySection title="2. Information we collect">
        <p>Depending on how you use the site, we may collect:</p>
        <ul>
          <li><strong>Contact and identity details</strong>, such as name, email address, telephone number, and billing or delivery address.</li>
          <li><strong>Order information</strong>, including items selected, size or style, price, discounts, delivery method, order status, returns, and customer-service history.</li>
          <li><strong>Payment and transaction details</strong>, such as payment status, billing details, transaction identifiers, and limited card information supplied by a payment provider. Full payment credentials may be collected directly by the payment provider available at checkout.</li>
          <li><strong>Communications</strong>, including messages, reviews, survey responses, promotion entries, and information you choose to send us.</li>
          <li><strong>Device and usage data</strong>, such as IP address, browser and device type, language, referring page, pages viewed, approximate location derived from IP address, timestamps, and diagnostic or security logs.</li>
          <li><strong>Preferences</strong>, including saved cart choices, communication choices, cookie choices, and interests inferred from interactions with our site where permitted.</li>
          <li><strong>Business-customer details</strong> if you make a wholesale or retailer inquiry, such as company name, business contact details, tax identifiers, and billing information.</li>
        </ul>
        <p>
          Please do not send sensitive information that we have not requested. If you
          provide information about another person, you must be authorized to do so.
        </p>
      </PolicySection>

      <PolicySection title="3. How information is collected">
        <p>
          We collect information directly from you, automatically from your browser or
          device, and from parties that help operate the store, such as payment,
          delivery, fraud-prevention, analytics, and customer-service providers. We may
          also receive information from advertising or social platforms when you
          interact with our content and the platform permits the disclosure.
        </p>
      </PolicySection>

      <PolicySection title="4. Why we use information">
        <ul>
          <li>to operate the site, maintain a cart, accept and fulfill orders, process payments, and provide delivery and return support;</li>
          <li>to communicate about an order, answer questions, and provide customer service;</li>
          <li>to authenticate activity, detect errors, prevent fraud, protect accounts and transactions, and enforce our terms;</li>
          <li>to personalize and improve products, site content, navigation, and service performance;</li>
          <li>to send marketing you requested or that is otherwise permitted, and to measure campaigns where allowed;</li>
          <li>to maintain business, tax, accounting, dispute, and compliance records; and</li>
          <li>to comply with law, lawful requests, and obligations that apply to us.</li>
        </ul>
        <p>
          Where a legal basis is required, we rely on performance of a contract,
          compliance with legal obligations, our legitimate interests (including store
          operation, security, improvement, and appropriate marketing), or consent.
          You may withdraw consent at any time; withdrawal does not affect earlier
          processing.
        </p>
      </PolicySection>

      <PolicySection title="5. When we disclose information">
        <p>We may disclose relevant information to:</p>
        <ul>
          <li>hosting, commerce, payment, fraud-prevention, fulfillment, delivery, communications, analytics, advertising, and customer-support providers;</li>
          <li>professional advisers, insurers, auditors, banks, and authorities where reasonably necessary;</li>
          <li>a buyer, investor, or successor in connection with a proposed or completed business transaction, subject to appropriate safeguards; and</li>
          <li>other parties when you direct us, consent, or when disclosure is reasonably necessary to protect rights, safety, property, transactions, or comply with law.</li>
        </ul>
        <p>
          We do not sell personal information for money. Some privacy laws define
          “sale,” “sharing,” or targeted advertising broadly enough to cover certain
          advertising-cookie disclosures. Where those activities occur, you may opt
          out as described under “Your privacy choices.”
        </p>
      </PolicySection>

      <PolicySection title="6. Cookies and similar technologies">
        <p>
          Cookies, pixels, local storage, and similar tools may keep the cart working,
          remember preferences, help secure checkout, measure site use, and—where
          enabled and permitted—support advertising. See our <Link to="/cookies">Cookie Policy</Link> for categories and controls.
        </p>
      </PolicySection>

      <PolicySection title="7. Retention">
        <p>
          We retain information only for as long as reasonably needed for the purposes
          above. The period depends on the type of record, whether an order remains
          active, warranty and return periods, security needs, consent status, and
          applicable tax, accounting, consumer-protection, limitation, and recordkeeping
          requirements. We then delete or de-identify information when reasonably
          practicable.
        </p>
      </PolicySection>

      <PolicySection title="8. Security">
        <p>
          We use reasonable administrative, technical, and organizational safeguards
          appropriate to the nature of the information. No internet transmission or
          storage system is completely secure, so we cannot promise absolute security.
          Do not send full payment-card details by email. Learn more in our <Link to="/security">Security &amp; Fraud Policy</Link>.
        </p>
      </PolicySection>

      <PolicySection title="9. International data transfers">
        <p>
          Our providers and operations may process information in countries other than
          your own. Privacy laws in those locations may differ. Where required, we use
          recognized safeguards for restricted transfers, such as contractual clauses,
          adequacy mechanisms, or another lawful transfer method.
        </p>
      </PolicySection>

      <PolicySection title="10. Your privacy choices">
        <p>Subject to local law and relevant exceptions, you may be entitled to:</p>
        <ul>
          <li>request access to, correction of, or deletion of personal information;</li>
          <li>request a portable copy or restriction of certain processing;</li>
          <li>object to processing based on legitimate interests or direct marketing;</li>
          <li>withdraw consent and unsubscribe from promotional email using the link in the message;</li>
          <li>opt out of certain targeted advertising, sale, or sharing as those terms are defined by applicable law; and</li>
          <li>appeal a refusal where a local privacy law provides that right.</li>
        </ul>
        <p>
          Visit our <Link to="/privacy-choices">Privacy Choices</Link> page or email
          <ContactEmail subject="Privacy request" />. Describe your request and the
          country or state where you live. We may verify your identity and authority
          before acting. Authorized agents may submit requests when permitted, but we
          may request proof of authorization. We do not discriminate against you for
          exercising a privacy right.
        </p>
        <p>
          Browser-based opt-out signals, including Global Privacy Control, will be
          handled where required and where the site can detect them. Because browser
          settings and identifiers differ, choices may need to be made again on another
          browser or device.
        </p>
      </PolicySection>

      <PolicySection title="11. Regional information">
        <h3>EEA, United Kingdom, and Switzerland</h3>
        <p>
          You may have the rights listed above and the right to complain to your local
          data-protection authority. If processing is based on legitimate interests,
          you may object based on your circumstances; you may always object to direct
          marketing.
        </p>
        <h3>United States</h3>
        <p>
          Residents of states with comprehensive privacy laws may request the rights
          available in their state, which can include access, correction, deletion,
          portability, and opt-out rights. We will use and disclose sensitive personal
          information, if any is collected, only for permitted business purposes unless
          we provide a separate notice.
        </p>
        <p>
          California residents may also request information about certain disclosures
          for direct-marketing purposes. Sacuddin does not disclose personal information
          to third parties for their own direct marketing without permission.
        </p>
        <h3>Other regions</h3>
        <p>
          Additional local rights may apply. Contact us and we will assess the request
          under the law applicable to you.
        </p>
      </PolicySection>

      <PolicySection title="12. Children">
        <p>
          The site is intended for a general audience and not directed to children
          under 16. We do not knowingly collect personal information from children
          under 16. A parent or guardian who believes a child provided information
          should contact us so we can take appropriate action.
        </p>
      </PolicySection>

      <PolicySection title="13. Changes to this policy">
        <p>
          We may revise this policy to reflect operational, legal, or regulatory
          changes. We will post the revised version here with a new “Last updated” date
          and provide additional notice where required.
        </p>
      </PolicySection>

      <PolicySection title="14. Contact">
        <p>
          Send privacy questions or requests to <ContactEmail subject="Privacy request" />.
          For faster handling, use the subject line “Privacy Request” and identify the
          right you wish to exercise.
        </p>
      </PolicySection>
    </PolicyPage>
  );
}

export default Privacy;
